import React, { useEffect, useState } from "react";
import allApi from "../../../api/allApi";
import { useNetwork } from "../common/networkContext";
import Loader from "../common/Loader";
import { useLocalization } from "../localization/LocalizationContext";

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

function Notification() {
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedNetworkId?.id) fetchNotifications();
  }, [selectedNetworkId]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      if (!networkId) return;
      const api = new allApi();
      const response = await api.getRequest(
        `api/notification/allNotifications?networkId=${networkId}`
      );
      const allFetchedNotifications = (response || []).map((notification) => ({
        id: notification.id || notification.notificationId,
        notificationName: notification.notificationName || notification.title,
        description: notification.description,
        createdAt: notification.createdAt,
        deviceName: notification.deviceName || "N/A",
        status: notification.status || 0,
      }));

      const unreadNotifications = allFetchedNotifications.filter(
        (notification) => notification.status === 0
      );
      setNotifications(allFetchedNotifications);
      setNotificationCount(unreadNotifications.length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const groupedNotifications = notifications.reduce((acc, notification) => {
    const dateKey = formatDate(notification.createdAt);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(notification);
    return acc;
  }, {});

  const markAsRead = async (notificationIds) => {
    try {
      const api = new allApi();
      await api.postRequest(
        `api/notification/markNotifications?networkId=${networkId}`,
        "",
        notificationIds
      );

      const updatedNotifications = notifications.map((item) =>
        notificationIds.includes(item.id) ? { ...item, status: 1 } : item
      );
      setNotifications(updatedNotifications);
      setNotificationCount(updatedNotifications.filter((item) => item.status === 0).length);
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const handleSingleRead = (notificationId) => {
    const notification = notifications.find((item) => item.id === notificationId);
    if (notification && notification.status === 0) {
      markAsRead([notificationId]);
    }
  };

  const unreadIds = notifications.filter((item) => item.status === 0).map((item) => item.id);

  return (
    <div className="row">
        <div className="col-lg-12">
          <div className="inc-card ch-100">
            <div className="inc-card-title">
              <h3>{localizationStrings?.notifications || "Notifications"}</h3>
              <div className="inc-card-button">
                <button
                  type="button"
                  className="text-btn"
                  disabled={!unreadIds.length}
                  onClick={() => markAsRead(unreadIds)}
                >
                  {localizationStrings?.markAllRead || "Mark all read"}
                </button>
              </div>
            </div>
            <div className="inc-card-body">
              {loading ? (
                <Loader />
              ) : Object.keys(groupedNotifications).length > 0 ? (
                <div className="notification-container w-scroll">
                  {Object.entries(groupedNotifications).map(([date, items]) => (
                    <div key={date} className="notification-group">
                      <h4>
                        {date} <span>{items.length}</span>
                      </h4>
                      {items.map((notification) => (
                        <div
                          key={notification.id}
                          className={`notification-list ${
                            notification.status === 0 ? "unread" : "read"
                          }`}
                          onClick={() => handleSingleRead(notification.id)}
                        >
                          <div className="n-info">
                            <h6>
                              {notification.notificationName ||
                                notification.deviceName ||
                                "N/A"}
                              <small>{formatTime(notification.createdAt)}</small>
                            </h6>
                            <p>{notification.description || localizationStrings?.noData || "No description found"}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="nms-empty">
                  {localizationStrings?.noNotifications || "No notifications available."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  );
}

export default Notification;
